import { MouseEvent, ReactNode, useState } from 'react';

import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useCopyToClipboard, useTimeoutFn } from 'react-use';
import classNames from 'classnames';

import { CheckGreyIcon, ReportSheetIcon } from 'assets/icons';

const COPIED_FEEDBACK_MS = 1500;

interface ICopyableTextProps {
  children: ReactNode;
  value?: string;
  className?: string;
  dataTestId?: string;
}

const CopyableText = ({ children, value, className, dataTestId }: ICopyableTextProps) => {
  const { formatMessage } = useIntl();
  const [copied, setCopied] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const [, , restartResetTimer] = useTimeoutFn(() => setCopied(false), COPIED_FEEDBACK_MS);

  const resolveValue = () => {
    if (value !== undefined) return value;
    return typeof children === 'string' || typeof children === 'number' ? String(children) : '';
  };

  const handleCopy = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const text = resolveValue();
    if (!text) return;

    copyToClipboard(text);
    setCopied(true);
    restartResetTimer();
  };

  return (
    <span
      data-test={dataTestId}
      className={classNames(
        'copyable-text d-inline-flex align-items-center rounded px-1 gap-1',
        className
      )}
    >
      {children}
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="copyable-text-tooltip">
            {formatMessage({ id: copied ? 'copiedKey' : 'copyKey' })}
          </Tooltip>
        }
      >
        <button
          type="button"
          data-test={dataTestId ? `${dataTestId}-copy-btn` : undefined}
          aria-label={formatMessage({ id: 'copyKey' })}
          onClick={handleCopy}
          className={classNames(
            'copyable-text-btn btn btn-link p-0 d-inline-flex align-items-center border-0',
            copied ? 'text-success is-copied' : 'text-gray-550'
          )}
        >
          {copied ? (
            <CheckGreyIcon className="icon" style={{ fontSize: '0.75rem' }} />
          ) : (
            <ReportSheetIcon className="icon" style={{ fontSize: '0.75rem' }} />
          )}
        </button>
      </OverlayTrigger>
    </span>
  );
};

export default CopyableText;
